import { container } from "tsyringe";
import { nextTick } from "vue";

// shared
import type { ServerErrorResponse } from "@/shared/libs/http/axios/interface";
import ConfirmLibsError from "@/shared/libs/http/axios/ConfirmAxiosError";
import { usePopupManager } from "@/shared/composables/usePopupManager";
import { getRefreshToken } from "@/shared/libs/state-management/token-manage";

import AlertUnExpectedError from "@/shared/components/popup/AlertUnExpectedError.vue";

// 예외입니다
import AlertInvalidTokenError from "@/features/authentication/ui/AlertInvalidTokenError.vue";
import { CommonAccountsBehavior } from "@/entities/common/interface/common-accounts/domain/behaviors/CommonAccountsBehavior";

// type
import type { AlertUnExpectedErrorPopupProps } from "@/shared/plugins/interface";

export const useQueryErrorCapture = () => {
    // instances
    const confirmLibsError = container.resolve(ConfirmLibsError);
    const commonAccountsBehavior = container.resolve(CommonAccountsBehavior);

    const { openPopup } = usePopupManager();

    /**
     * Captures and handles query errors with popup notifications
     * @param error - The error to capture
     * @throws {Error} - Rethrows the error after showing popup (asynchronously after nextTick)
     */
    const capture = async (error: unknown) => {
        if (confirmLibsError.isAxiosError<ServerErrorResponse>(error)) {
            if (error.name === "CanceledError") {
                // Canceled errors are expected and should not be propagated
                return;
            }
            const errorResponse = error.response?.data;

            /** code 가 지정되어있지 않는 에러에 한하여 throw */
            if (!errorResponse?.errorCode) {
                openPopup<AlertUnExpectedErrorPopupProps>({
                    component: AlertUnExpectedError,
                    props: {
                        closeKey: "",
                        rest: {
                            title: `관리자에게 문의해주세요.`,
                            message: error.message,
                        },
                    },
                });
                await nextTick();
                throw error;
            }

            /** accessToken 이상 시  */
            if (commonAccountsBehavior.checkAccessTokenError(errorResponse)) {
                if (getRefreshToken()) {
                    return;
                }

                console.log("💥 accessToken 이상 시 팝업 표시");
                openPopup({
                    component: AlertInvalidTokenError,
                    props: {
                        closeKey: "invalid-token-error",
                        rest: {
                            title: "로그인 정보를 갱신해야합니다. 로그인 페이지로 이동합니다.",
                        },
                    },
                });

                return;
            }

            openPopup<AlertUnExpectedErrorPopupProps>({
                component: AlertUnExpectedError,
                props: {
                    closeKey: "",
                    rest: {
                        title: `관리자에게 문의해주세요.`,
                        message: error.message,
                    },
                },
            });
            await nextTick();
            throw error;
        } else {
            if (error instanceof Error) {
                openPopup<AlertUnExpectedErrorPopupProps>({
                    component: AlertUnExpectedError,
                    props: {
                        closeKey: "",
                        rest: {
                            title: `관리자에게 문의해주세요.`,
                            message: error.message,
                        },
                    },
                });
                await nextTick();
                throw error;
            } else {
                throw error;
            }
        }
    };

    return {
        capture,
    };
};
